import { db, storage } from '../lib/firebase';
import {
  collection,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  query,
  where,
  orderBy,
  getDocs,
  getDoc,
  serverTimestamp,
  writeBatch,
  increment
} from 'firebase/firestore';
import { ref, uploadString, getDownloadURL } from 'firebase/storage';

/**
 * Library Service
 * Handles CRUD operations for library items (casting, locations, creatives)
 * Items are scoped by userId and brandId
 */

// Collection name
const LIBRARY_COLLECTION = 'libraryItems';

/**
 * Check if a string is a base64 data URL
 */
function isBase64DataUrl(str) {
  return typeof str === 'string' && str.startsWith('data:image');
}

/**
 * Recursively remove undefined values from an object
 * Firestore throws an error if any field value is undefined
 */
function removeUndefinedValues(obj) {
  if (obj === null || obj === undefined) {
    return null;
  }

  if (Array.isArray(obj)) {
    return obj.map(item => removeUndefinedValues(item)).filter(item => item !== undefined);
  }

  if (typeof obj === 'object' && obj !== null) {
    const cleaned = {};
    for (const [key, value] of Object.entries(obj)) {
      if (value !== undefined) {
        cleaned[key] = removeUndefinedValues(value);
      }
    }
    return cleaned;
  }

  return obj;
}

/**
 * Save a single library item
 * @param {Object} item - The item data
 * @param {string} userId - The user ID
 * @param {string} brandId - The brand ID
 * @returns {Promise<string>} - The document ID
 */
export async function saveLibraryItem(item, userId, brandId) {
  if (!userId || !brandId) {
    throw new Error('userId and brandId are required');
  }

  // Upload base64 image to storage if needed
  let imageUrl = item.imageUrl;
  if (isBase64DataUrl(imageUrl)) {
    imageUrl = await uploadLibraryImage(imageUrl, userId, brandId, item.type || 'unknown');
  }

  // Clean the item of undefined values (Firestore rejects undefined)
  const cleanedItem = removeUndefinedValues(item);

  const itemDoc = {
    ...cleanedItem,
    imageUrl,
    userId,
    brandId,
    isFavorite: cleanedItem.isFavorite || false,
    tags: cleanedItem.tags || [],
    usageCount: cleanedItem.usageCount || 0,
    lastUsedAt: cleanedItem.lastUsedAt || null,
    createdAt: cleanedItem.createdAt || serverTimestamp(),
    updatedAt: serverTimestamp()
  };

  // Remove id if it exists (Firestore will generate one)
  delete itemDoc.id;

  const docRef = await addDoc(collection(db, LIBRARY_COLLECTION), itemDoc);
  return docRef.id;
}

/**
 * Save an Ad Creative
 * @param {Object} adData - The ad creative data
 * @param {string} userId - The user ID
 * @param {string} brandId - The brand ID
 * @returns {Promise<string>} - The document ID
 */
export async function saveAdCreative(adData, userId, brandId) {
  // Ensure correct type and metadata for ad creatives
  const adCreative = {
    ...adData,
    type: 'ad_creative', // Explicit type for ads
    isAdCreative: true,
    // Ensure metadata structure
    metadata: {
      ...adData.metadata,
      caption: adData.description || adData.metadata?.caption,
      hashtags: Array.isArray(adData.tags) ? adData.tags.join(' ') : adData.metadata?.hashtags,
      pillarId: adData.pillarId,
      pillarName: adData.pillarName
    }
  };

  return saveLibraryItem(adCreative, userId, brandId);
}

/**
 * Batch save multiple library items
 * @param {Array} items - Array of items to save
 * @param {string} userId - The user ID
 * @param {string} brandId - The brand ID
 * @returns {Promise<Array<string>>} - Array of document IDs
 */
export async function batchSaveItems(items, userId, brandId) {
  if (!userId || !brandId) {
    throw new Error('userId and brandId are required');
  }

  if (!items || items.length === 0) {
    return [];
  }

  // First, upload any base64 images to storage (can't do async in batch)
  const processedItems = await Promise.all(
    items.map(async (item) => {
      let imageUrl = item.imageUrl;
      if (isBase64DataUrl(imageUrl)) {
        imageUrl = await uploadLibraryImage(imageUrl, userId, brandId, item.type || 'unknown');
      }
      return { ...item, imageUrl };
    })
  );

  const batch = writeBatch(db);
  const docRefs = [];

  for (const item of processedItems) {
    const docRef = doc(collection(db, LIBRARY_COLLECTION));
    docRefs.push(docRef.id);

    // Clean the item of undefined values (Firestore rejects undefined)
    const cleanedItem = removeUndefinedValues(item);

    const itemDoc = {
      ...cleanedItem,
      userId,
      brandId,
      isFavorite: cleanedItem.isFavorite || false,
      tags: cleanedItem.tags || [],
      usageCount: cleanedItem.usageCount || 0,
      lastUsedAt: cleanedItem.lastUsedAt || null,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    };

    // Remove id if it exists
    delete itemDoc.id;

    batch.set(docRef, itemDoc);
  }

  await batch.commit();
  return docRefs;
}

/**
 * Save a creative with its A/B test variants as a variant group
 * @param {Object} baseItem - The base creative item
 * @param {Object} variantData - The variant configuration from VariantLab
 * @param {string} userId - The user ID
 * @param {string} brandId - The brand ID
 * @returns {Promise<Object>} - { baseItemId, variantItemIds, variantGroupId }
 */
export async function saveCreativeWithVariants(baseItem, variantData, userId, brandId) {
  if (!userId || !brandId) {
    throw new Error('userId and brandId are required');
  }

  // Generate a unique variant group ID
  const variantGroupId = `vg_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

  // Prepare all items with variant metadata
  const itemsToSave = [];

  // Add the base item (control variant)
  const controlVariant = variantData?.variants?.find(v => v.isControl);
  itemsToSave.push({
    ...baseItem,
    variantGroupId,
    variantId: controlVariant?.id || 'A',
    variantLabel: controlVariant?.label || 'Control',
    isControlVariant: true,
    testElement: variantData?.testElement || null,
    testDescription: variantData?.testDescription || null,
    hypothesis: controlVariant?.hypothesis || null,
    variantChanges: controlVariant?.changes || {},
    recommendedMetrics: variantData?.recommendedMetrics || [],
    minimumSampleSize: variantData?.minimumSampleSize || 1000,
    recommendedDuration: variantData?.recommendedDuration || '7-14 days'
  });

  // Add other variants
  const otherVariants = variantData?.variants?.filter(v => !v.isControl) || [];
  for (const variant of otherVariants) {
    // For visual variants, the imageUrl might be different
    // For copy/headline variants, merge the changes with the base item
    const variantItem = {
      ...baseItem,
      variantGroupId,
      variantId: variant.id,
      variantLabel: variant.label,
      isControlVariant: false,
      testElement: variantData?.testElement || null,
      testDescription: variantData?.testDescription || null,
      hypothesis: variant.hypothesis,
      variantChanges: variant.changes || {},
      expectedOutcome: variant.expectedOutcome,
      // Apply changes to the base item
      title: variant.changes?.headline || baseItem.title,
      description: variant.changes?.primaryText || baseItem.description,
      cta: variant.changes?.cta || baseItem.cta,
      marketingAngle: variant.changes?.marketingAngle || baseItem.marketingAngle
    };
    itemsToSave.push(variantItem);
  }

  // Save all items as a batch
  const savedIds = await batchSaveItems(itemsToSave, userId, brandId);

  return {
    baseItemId: savedIds[0],
    variantItemIds: savedIds.slice(1),
    variantGroupId,
    totalVariants: savedIds.length
  };
}

/**
 * Get all items in a variant group
 * @param {string} variantGroupId - The variant group ID
 * @param {string} userId - The user ID
 * @param {string} brandId - The brand ID
 * @returns {Promise<Array>} - Array of variant items
 */
export async function getVariantGroup(variantGroupId, userId, brandId) {
  if (!variantGroupId || !userId || !brandId) {
    return [];
  }

  const q = query(
    collection(db, LIBRARY_COLLECTION),
    where('userId', '==', userId),
    where('brandId', '==', brandId),
    where('variantGroupId', '==', variantGroupId),
    orderBy('variantId', 'asc')
  );

  const snapshot = await getDocs(q);
  return snapshot.docs.map(doc => ({
    id: doc.id,
    ...doc.data()
  }));
}

/**
 * Save OneDrive-linked library items
 * Creates library items that reference files in OneDrive (no local copy)
 * @param {Array} oneDriveFiles - Array of OneDrive file objects
 * @param {string} userId - The user ID
 * @param {string} brandId - The brand ID
 * @param {string} itemType - The item type ('image', 'casting', 'location')
 * @returns {Promise<Array<string>>} - Array of document IDs
 */
export async function saveOneDriveItems(oneDriveFiles, userId, brandId, itemType = 'image') {
  if (!userId || !brandId) {
    throw new Error('userId and brandId are required');
  }

  if (!oneDriveFiles || oneDriveFiles.length === 0) {
    return [];
  }

  const batch = writeBatch(db);
  const docRefs = [];

  for (const file of oneDriveFiles) {
    const docRef = doc(collection(db, LIBRARY_COLLECTION));
    docRefs.push(docRef.id);

    // Extract name without extension for title
    const nameWithoutExt = file.name.replace(/\.[^/.]+$/, '');

    const itemDoc = {
      title: nameWithoutExt,
      name: file.name,
      description: '',
      type: itemType,
      // Use thumbnail URL as imageUrl for display
      imageUrl: file.thumbnail || file.downloadUrl,
      userId,
      brandId,
      isFavorite: false,
      tags: [],
      usageCount: 0,
      lastUsedAt: null,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      // OneDrive-specific metadata
      sourceType: 'onedrive',
      oneDriveMetadata: {
        fileId: file.id,
        fileName: file.name,
        webUrl: file.webUrl,
        downloadUrl: file.downloadUrl,
        thumbnailUrl: file.thumbnail,
        mimeType: file.mimeType,
        size: file.size,
        linkedAt: new Date().toISOString()
      }
    };

    batch.set(docRef, itemDoc);
  }

  await batch.commit();
  return docRefs;
}

/**
 * Load library items with optional filters
 * @param {string} userId - The user ID
 * @param {string} brandId - The brand ID
 * @param {Object} options - Filter options
 * @param {string} [options.type] - Filter by type ('casting', 'location', 'creative')
 * @param {boolean} [options.isFavorite] - Filter by favorite status
 * @param {Array<string>} [options.tags] - Filter by tags (items must have ALL specified tags)
 * @param {number} [options.limit] - Maximum number of items to return
 * @param {string} [options.orderByField] - Field to order by ('createdAt', 'usageCount', 'lastUsedAt')
 * @param {string} [options.planId] - Filter by content plan ID (from bulk generation)
 * @returns {Promise<Array>} - Array of library items
 */
export async function loadLibraryItems(userId, brandId, options = {}) {
  if (!userId || !brandId) {
    throw new Error('userId and brandId are required');
  }

  const { type, isFavorite, tags, limit: queryLimit, orderByField = 'createdAt', planId, productName } = options;

  // Build query constraints
  let constraints = [
    where('userId', '==', userId),
    where('brandId', '==', brandId)
  ];

  // Only use single type filter in query (array filtering done in memory to avoid composite index requirement)
  if (type && !Array.isArray(type)) {
    constraints.push(where('type', '==', type));
  }

  if (isFavorite !== undefined) {
    constraints.push(where('isFavorite', '==', isFavorite));
  }

  // Add ordering
  constraints.push(orderBy(orderByField, 'desc'));

  const q = query(collection(db, LIBRARY_COLLECTION), ...constraints);
  const querySnapshot = await getDocs(q);

  let items = [];
  querySnapshot.forEach((doc) => {
    items.push({
      id: doc.id,
      ...doc.data()
    });
  });

  // Filter by type array in memory (avoids composite index requirement for 'in' + orderBy)
  if (type && Array.isArray(type)) {
    items = items.filter(item => type.includes(item.type));
  }

  // Filter by tags in memory (Firestore doesn't support array-contains-all)
  if (tags && tags.length > 0) {
    items = items.filter(item =>
      tags.every(tag => item.tags && item.tags.includes(tag))
    );
  }

  // Filter by planId in memory (nested field in metadata)
  if (planId) {
    items = items.filter(item =>
      item.metadata?.planId === planId
    );
  }

  // Filter by productName in memory (nested field in metadata)
  if (productName) {
    items = items.filter(item =>
      item.metadata?.productName === productName
    );
  }

  // Apply limit after tag filtering
  if (queryLimit && items.length > queryLimit) {
    items = items.slice(0, queryLimit);
  }

  return items;
}

/**
 * Get favorite items for a specific type (optimized for chat selection)
 * @param {string} userId - The user ID
 * @param {string} brandId - The brand ID
 * @param {string} type - The item type ('casting', 'location')
 * @returns {Promise<Array>} - Array of favorite items
 */
export async function getFavorites(userId, brandId, type) {
  return loadLibraryItems(userId, brandId, {
    type,
    isFavorite: true,
    orderByField: 'lastUsedAt'
  });
}

/**
 * Get a single library item by ID
 * @param {string} itemId - The item document ID
 * @returns {Promise<Object|null>} - The item data or null if not found
 */
export async function getLibraryItem(itemId) {
  const docRef = doc(db, LIBRARY_COLLECTION, itemId);
  const docSnap = await getDoc(docRef);

  if (!docSnap.exists()) {
    return null;
  }

  return {
    id: docSnap.id,
    ...docSnap.data()
  };
}

/**
 * Toggle favorite status for an item
 * @param {string} itemId - The item document ID
 * @param {string} userId - The user ID (for verification)
 * @returns {Promise<boolean>} - The new favorite status
 */
export async function toggleFavorite(itemId, userId) {
  const docRef = doc(db, LIBRARY_COLLECTION, itemId);
  const docSnap = await getDoc(docRef);

  if (!docSnap.exists()) {
    throw new Error('Item not found');
  }

  const item = docSnap.data();

  // Verify ownership
  if (item.userId !== userId) {
    throw new Error('Unauthorized: You do not own this item');
  }

  const newFavoriteStatus = !item.isFavorite;

  await updateDoc(docRef, {
    isFavorite: newFavoriteStatus,
    updatedAt: serverTimestamp()
  });

  return newFavoriteStatus;
}

/**
 * Update tags for an item
 * @param {string} itemId - The item document ID
 * @param {Array<string>} tags - The new tags array
 * @param {string} userId - The user ID (for verification)
 * @returns {Promise<void>}
 */
export async function updateItemTags(itemId, tags, userId) {
  const docRef = doc(db, LIBRARY_COLLECTION, itemId);
  const docSnap = await getDoc(docRef);

  if (!docSnap.exists()) {
    throw new Error('Item not found');
  }

  const item = docSnap.data();

  // Verify ownership
  if (item.userId !== userId) {
    throw new Error('Unauthorized: You do not own this item');
  }

  // Normalize tags (lowercase, trimmed)
  const normalizedTags = tags
    .map(tag => tag.toLowerCase().trim())
    .filter(tag => tag.length > 0);

  await updateDoc(docRef, {
    tags: normalizedTags,
    updatedAt: serverTimestamp()
  });
}

/**
 * Delete a library item
 * @param {string} itemId - The item document ID
 * @param {string} userId - The user ID (for verification)
 * @returns {Promise<void>}
 */
export async function deleteLibraryItem(itemId, userId) {
  const docRef = doc(db, LIBRARY_COLLECTION, itemId);
  const docSnap = await getDoc(docRef);

  if (!docSnap.exists()) {
    throw new Error('Item not found');
  }

  const item = docSnap.data();

  // Verify ownership
  if (item.userId !== userId) {
    throw new Error('Unauthorized: You do not own this item');
  }

  await deleteDoc(docRef);
}

/**
 * Increment usage count for an item
 * @param {string} itemId - The item document ID
 * @returns {Promise<void>}
 */
export async function incrementUsage(itemId) {
  const docRef = doc(db, LIBRARY_COLLECTION, itemId);

  await updateDoc(docRef, {
    usageCount: increment(1),
    lastUsedAt: serverTimestamp(),
    updatedAt: serverTimestamp()
  });
}

/**
 * Get all unique tags for a brand
 * @param {string} userId - The user ID
 * @param {string} brandId - The brand ID
 * @returns {Promise<Array<{name: string, count: number}>>} - Array of tags with counts
 */
export async function getBrandTags(userId, brandId) {
  const items = await loadLibraryItems(userId, brandId, {});

  // Count tag occurrences
  const tagCounts = {};
  items.forEach(item => {
    if (item.tags && Array.isArray(item.tags)) {
      item.tags.forEach(tag => {
        tagCounts[tag] = (tagCounts[tag] || 0) + 1;
      });
    }
  });

  // Convert to array and sort by count
  return Object.entries(tagCounts)
    .map(([name, count]) => ({ name, count }))
    .sort((a, b) => b.count - a.count);
}

/**
 * Add a tag to an item
 * @param {string} itemId - The item document ID
 * @param {string} tagName - The tag to add
 * @param {string} userId - The user ID (for verification)
 * @returns {Promise<void>}
 */
export async function addTagToItem(itemId, tagName, userId) {
  const docRef = doc(db, LIBRARY_COLLECTION, itemId);
  const docSnap = await getDoc(docRef);

  if (!docSnap.exists()) {
    throw new Error('Item not found');
  }

  const item = docSnap.data();

  // Verify ownership
  if (item.userId !== userId) {
    throw new Error('Unauthorized: You do not own this item');
  }

  const normalizedTag = tagName.toLowerCase().trim();
  const currentTags = item.tags || [];

  if (!currentTags.includes(normalizedTag)) {
    await updateDoc(docRef, {
      tags: [...currentTags, normalizedTag],
      updatedAt: serverTimestamp()
    });
  }
}

/**
 * Remove a tag from an item
 * @param {string} itemId - The item document ID
 * @param {string} tagName - The tag to remove
 * @param {string} userId - The user ID (for verification)
 * @returns {Promise<void>}
 */
export async function removeTagFromItem(itemId, tagName, userId) {
  const docRef = doc(db, LIBRARY_COLLECTION, itemId);
  const docSnap = await getDoc(docRef);

  if (!docSnap.exists()) {
    throw new Error('Item not found');
  }

  const item = docSnap.data();

  // Verify ownership
  if (item.userId !== userId) {
    throw new Error('Unauthorized: You do not own this item');
  }

  const normalizedTag = tagName.toLowerCase().trim();
  const currentTags = item.tags || [];

  await updateDoc(docRef, {
    tags: currentTags.filter(t => t !== normalizedTag),
    updatedAt: serverTimestamp()
  });
}

/**
 * Rename a tag across all items for a brand
 * @param {string} oldTagName - The old tag name
 * @param {string} newTagName - The new tag name
 * @param {string} userId - The user ID
 * @param {string} brandId - The brand ID
 * @returns {Promise<number>} - Number of items updated
 */
export async function renameTag(oldTagName, newTagName, userId, brandId) {
  const normalizedOld = oldTagName.toLowerCase().trim();
  const normalizedNew = newTagName.toLowerCase().trim();

  if (normalizedOld === normalizedNew) {
    return 0;
  }

  // Get all items with the old tag
  const items = await loadLibraryItems(userId, brandId, { tags: [normalizedOld] });

  if (items.length === 0) {
    return 0;
  }

  const batch = writeBatch(db);

  items.forEach(item => {
    const docRef = doc(db, LIBRARY_COLLECTION, item.id);
    const newTags = item.tags
      .map(t => t === normalizedOld ? normalizedNew : t)
      .filter((t, i, arr) => arr.indexOf(t) === i); // Remove duplicates

    batch.update(docRef, {
      tags: newTags,
      updatedAt: serverTimestamp()
    });
  });

  await batch.commit();
  return items.length;
}

/**
 * Delete a tag from all items for a brand
 * @param {string} tagName - The tag to delete
 * @param {string} userId - The user ID
 * @param {string} brandId - The brand ID
 * @returns {Promise<number>} - Number of items updated
 */
export async function deleteTagFromAll(tagName, userId, brandId) {
  const normalizedTag = tagName.toLowerCase().trim();

  // Get all items with this tag
  const items = await loadLibraryItems(userId, brandId, { tags: [normalizedTag] });

  if (items.length === 0) {
    return 0;
  }

  const batch = writeBatch(db);

  items.forEach(item => {
    const docRef = doc(db, LIBRARY_COLLECTION, item.id);
    batch.update(docRef, {
      tags: item.tags.filter(t => t !== normalizedTag),
      updatedAt: serverTimestamp()
    });
  });

  await batch.commit();
  return items.length;
}

/**
 * Upload an image to Firebase Storage for library items
 * @param {string} imageDataUrl - Base64 data URL of the image
 * @param {string} userId - The user ID
 * @param {string} brandId - The brand ID
 * @param {string} itemType - The item type (casting, location, creative)
 * @returns {Promise<string>} - The download URL
 */
export async function uploadLibraryImage(imageDataUrl, userId, brandId, itemType) {
  // Determine file extension from data URL
  const mimeMatch = imageDataUrl.match(/data:image\/([^;]+);/);
  const extension = mimeMatch ? mimeMatch[1].replace('jpeg', 'jpg') : 'png';

  const imageId = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  const storageRef = ref(storage, `library-images/${userId}/${brandId}/${itemType}/${imageId}.${extension}`);

  await uploadString(storageRef, imageDataUrl, 'data_url');
  const downloadURL = await getDownloadURL(storageRef);

  return downloadURL;
}

/**
 * Check if an item with the same imageUrl already exists in the library
 * @param {string} userId - The user ID
 * @param {string} brandId - The brand ID
 * @param {string} imageUrl - The image URL to check
 * @returns {Promise<boolean>} - True if item exists
 */
export async function itemExists(userId, brandId, imageUrl) {
  const q = query(
    collection(db, LIBRARY_COLLECTION),
    where('userId', '==', userId),
    where('brandId', '==', brandId),
    where('imageUrl', '==', imageUrl)
  );

  const querySnapshot = await getDocs(q);
  return !querySnapshot.empty;
}

/**
 * Update a library item
 * @param {string} itemId - The item document ID
 * @param {Object} updates - The fields to update
 * @param {string} userId - The user ID (for verification)
 * @returns {Promise<void>}
 */
export async function updateLibraryItem(itemId, updates, userId) {
  const docRef = doc(db, LIBRARY_COLLECTION, itemId);
  const docSnap = await getDoc(docRef);

  if (!docSnap.exists()) {
    throw new Error('Item not found');
  }

  const item = docSnap.data();

  // Verify ownership
  if (item.userId !== userId) {
    throw new Error('Unauthorized: You do not own this item');
  }

  // Don't allow updating protected fields
  const { id, userId: uid, brandId, createdAt, ...safeUpdates } = updates;

  await updateDoc(docRef, {
    ...safeUpdates,
    updatedAt: serverTimestamp()
  });
}

/**
 * Initialize history for an item that doesn't have one yet
 * Creates the initial history entry from the current imageUrl
 * @param {string} itemId - The item document ID
 * @param {string} userId - The user ID (for verification)
 * @returns {Promise<Object>} - The updated item with history
 */
export async function initializeItemHistory(itemId, userId) {
  const docRef = doc(db, LIBRARY_COLLECTION, itemId);
  const docSnap = await getDoc(docRef);

  if (!docSnap.exists()) {
    throw new Error('Item not found');
  }

  const item = docSnap.data();

  // Verify ownership
  if (item.userId !== userId) {
    throw new Error('Unauthorized: You do not own this item');
  }

  // If history already exists, return the item as-is
  if (item.history && item.history.length > 0) {
    return { id: docSnap.id, ...item };
  }

  // Create initial history entry from current imageUrl
  const initialHistory = [{
    imageUrl: item.imageUrl,
    prompt: null,
    refinedAt: new Date(),
    isOriginal: true
  }];

  await updateDoc(docRef, {
    history: initialHistory,
    updatedAt: serverTimestamp()
  });

  return {
    id: docSnap.id,
    ...item,
    history: initialHistory
  };
}

/**
 * Add a refined version to an item's history
 * @param {string} itemId - The item document ID
 * @param {string} newImageUrl - The URL of the refined image (can be base64 data URL or storage URL)
 * @param {string} prompt - The refinement prompt used
 * @param {string} userId - The user ID (for verification)
 * @returns {Promise<Object>} - The updated item
 */
export async function addRefinedVersion(itemId, newImageUrl, prompt, userId) {
  const docRef = doc(db, LIBRARY_COLLECTION, itemId);
  const docSnap = await getDoc(docRef);

  if (!docSnap.exists()) {
    throw new Error('Item not found');
  }

  const item = docSnap.data();

  // Verify ownership
  if (item.userId !== userId) {
    throw new Error('Unauthorized: You do not own this item');
  }

  // Upload base64 image to storage if needed
  let finalImageUrl = newImageUrl;
  if (isBase64DataUrl(newImageUrl)) {
    finalImageUrl = await uploadLibraryImage(newImageUrl, userId, item.brandId, item.type || 'refined');
  }

  // Initialize history if needed
  let history = item.history || [];
  if (history.length === 0 && item.imageUrl) {
    history = [{
      imageUrl: item.imageUrl,
      prompt: null,
      refinedAt: new Date(),
      isOriginal: true
    }];
  }

  // Add new version to history
  const newVersion = {
    imageUrl: finalImageUrl,
    prompt: prompt,
    refinedAt: new Date(),
    isOriginal: false
  };
  history.push(newVersion);

  // Update item with new imageUrl and history
  await updateDoc(docRef, {
    imageUrl: finalImageUrl,
    history: history,
    updatedAt: serverTimestamp()
  });

  return {
    id: docSnap.id,
    ...item,
    imageUrl: finalImageUrl,
    history: history
  };
}

/**
 * Add a refined version to a specific carousel card's history
 * Each carousel card maintains its own history array
 * @param {string} itemId - The item document ID
 * @param {number} cardIndex - The index of the carousel card
 * @param {string} newImageUrl - The URL of the refined image
 * @param {string} prompt - The refinement prompt used
 * @param {string} userId - The user ID (for verification)
 * @param {Array} existingCarouselImages - Optional: carousel images from the result (used if library item doesn't have them)
 * @returns {Promise<Object>} - The updated item
 */
export async function addCarouselCardRefinedVersion(itemId, cardIndex, newImageUrl, prompt, userId, existingCarouselImages = null) {
  const docRef = doc(db, LIBRARY_COLLECTION, itemId);
  const docSnap = await getDoc(docRef);

  if (!docSnap.exists()) {
    throw new Error('Item not found');
  }

  const item = docSnap.data();

  // Verify ownership
  if (item.userId !== userId) {
    throw new Error('Unauthorized: You do not own this item');
  }

  // Get carousel images array - check root level, metadata, or use provided images from result
  let carouselImages = Array.isArray(item.carouselImages)
    ? item.carouselImages
    : Array.isArray(item.metadata?.carouselImages)
      ? item.metadata.carouselImages
      : Array.isArray(existingCarouselImages)
        ? existingCarouselImages
        : [];

  if (carouselImages.length === 0) {
    throw new Error('No carousel images found');
  }
  if (cardIndex < 0 || cardIndex >= carouselImages.length) {
    throw new Error(`Invalid card index: ${cardIndex}`);
  }

  // Upload base64 image to storage if needed
  let finalImageUrl = newImageUrl;
  if (isBase64DataUrl(newImageUrl)) {
    finalImageUrl = await uploadLibraryImage(newImageUrl, userId, item.brandId, item.type || 'carousel');
  }

  const card = carouselImages[cardIndex];
  const currentImageUrl = card.imageUrl || card.url;

  // Initialize history for this card if needed
  let cardHistory = Array.isArray(card.history) ? card.history : [];
  if (cardHistory.length === 0 && currentImageUrl) {
    cardHistory = [{
      imageUrl: currentImageUrl,
      prompt: null,
      refinedAt: new Date(),
      isOriginal: true
    }];
  }

  // Add new version to card's history
  const newVersion = {
    imageUrl: finalImageUrl,
    prompt: prompt,
    refinedAt: new Date(),
    isOriginal: false
  };
  cardHistory.push(newVersion);

  // Update the carousel card with new imageUrl and history
  const updatedCarouselImages = [...carouselImages];
  updatedCarouselImages[cardIndex] = {
    ...card,
    imageUrl: finalImageUrl,
    url: finalImageUrl,
    history: cardHistory
  };

  // Always save at root level (migrate from metadata if needed)
  await updateDoc(docRef, {
    carouselImages: updatedCarouselImages,
    updatedAt: serverTimestamp()
  });

  return {
    id: docSnap.id,
    ...item,
    carouselImages: updatedCarouselImages
  };
}

/**
 * Set active version for a carousel card from its history
 * @param {string} itemId - The item document ID
 * @param {number} cardIndex - The index of the carousel card
 * @param {number} historyIndex - The index in the card's history array
 * @param {string} userId - The user ID (for verification)
 * @returns {Promise<Object>} - The updated item
 */
export async function setCarouselCardActiveVersion(itemId, cardIndex, historyIndex, userId) {
  const docRef = doc(db, LIBRARY_COLLECTION, itemId);
  const docSnap = await getDoc(docRef);

  if (!docSnap.exists()) {
    throw new Error('Item not found');
  }

  const item = docSnap.data();

  // Verify ownership
  if (item.userId !== userId) {
    throw new Error('Unauthorized: You do not own this item');
  }

  // Get carousel images array - check both root level and metadata (legacy)
  const carouselImages = Array.isArray(item.carouselImages)
    ? item.carouselImages
    : Array.isArray(item.metadata?.carouselImages)
      ? item.metadata.carouselImages
      : [];

  if (carouselImages.length === 0) {
    throw new Error('No carousel images found in library item');
  }
  if (cardIndex < 0 || cardIndex >= carouselImages.length) {
    throw new Error(`Invalid card index: ${cardIndex}`);
  }

  const card = carouselImages[cardIndex];
  const cardHistory = Array.isArray(card.history) ? card.history : [];

  if (historyIndex < 0 || historyIndex >= cardHistory.length) {
    throw new Error(`Invalid history index: ${historyIndex}`);
  }

  const selectedVersion = cardHistory[historyIndex];

  // Update the carousel card with the selected version's image
  const updatedCarouselImages = [...carouselImages];
  updatedCarouselImages[cardIndex] = {
    ...card,
    imageUrl: selectedVersion.imageUrl,
    url: selectedVersion.imageUrl
  };

  await updateDoc(docRef, {
    carouselImages: updatedCarouselImages,
    updatedAt: serverTimestamp()
  });

  return {
    id: docSnap.id,
    ...item,
    carouselImages: updatedCarouselImages
  };
}

/**
 * Add an upscaled version to an item's history
 * @param {string} itemId - The item document ID
 * @param {string} upscaledImageUrl - The new upscaled image URL
 * @param {Object} upscaleMetadata - Metadata about the upscale operation
 * @param {string} upscaleMetadata.factor - The upscale factor (x2, x3, x4)
 * @param {Object} upscaleMetadata.originalDimensions - { width, height }
 * @param {Object} upscaleMetadata.upscaledDimensions - { width, height }
 * @param {string} userId - The user ID (for verification)
 * @returns {Promise<Object>} - The updated item with history
 */
export async function addUpscaledVersion(itemId, upscaledImageUrl, upscaleMetadata, userId) {
  const docRef = doc(db, LIBRARY_COLLECTION, itemId);
  const docSnap = await getDoc(docRef);

  if (!docSnap.exists()) {
    throw new Error('Item not found');
  }

  const item = docSnap.data();

  // Verify ownership
  if (item.userId !== userId) {
    throw new Error('Unauthorized: You do not own this item');
  }

  // Initialize history if needed
  let history = item.history || [];
  if (history.length === 0 && item.imageUrl) {
    history = [{
      imageUrl: item.imageUrl,
      prompt: null,
      refinedAt: new Date(),
      isOriginal: true
    }];
  }

  // Add upscaled version to history
  const newVersion = {
    imageUrl: upscaledImageUrl,
    prompt: `Upscaled ${upscaleMetadata.factor}`,
    refinedAt: new Date(),
    isOriginal: false,
    isUpscaled: true,
    upscaleMetadata: {
      factor: upscaleMetadata.factor,
      originalDimensions: upscaleMetadata.originalDimensions,
      upscaledDimensions: upscaleMetadata.upscaledDimensions
    }
  };
  history.push(newVersion);

  // Update item with new imageUrl and history
  await updateDoc(docRef, {
    imageUrl: upscaledImageUrl,
    history: history,
    updatedAt: serverTimestamp()
  });

  return {
    id: docSnap.id,
    ...item,
    imageUrl: upscaledImageUrl,
    history: history
  };
}

/**
 * Set a specific history version as the active image
 * @param {string} itemId - The item document ID
 * @param {number} historyIndex - The index in the history array to set as active
 * @param {string} userId - The user ID (for verification)
 * @returns {Promise<Object>} - The updated item
 */
export async function setActiveVersion(itemId, historyIndex, userId) {
  const docRef = doc(db, LIBRARY_COLLECTION, itemId);
  const docSnap = await getDoc(docRef);

  if (!docSnap.exists()) {
    throw new Error('Item not found');
  }

  const item = docSnap.data();

  // Verify ownership
  if (item.userId !== userId) {
    throw new Error('Unauthorized: You do not own this item');
  }

  const history = item.history || [];
  if (historyIndex < 0 || historyIndex >= history.length) {
    throw new Error('Invalid history index');
  }

  const selectedVersion = history[historyIndex];

  await updateDoc(docRef, {
    imageUrl: selectedVersion.imageUrl,
    updatedAt: serverTimestamp()
  });

  return {
    id: docSnap.id,
    ...item,
    imageUrl: selectedVersion.imageUrl
  };
}

/**
 * Split a history version into a new independent library item
 * @param {string} itemId - The source item document ID
 * @param {number} historyIndex - The index in the history array to split
 * @param {string} userId - The user ID
 * @param {string} brandId - The brand ID
 * @returns {Promise<string>} - The new item's document ID
 */
export async function splitHistoryVersion(itemId, historyIndex, userId, brandId) {
  const docRef = doc(db, LIBRARY_COLLECTION, itemId);
  const docSnap = await getDoc(docRef);

  if (!docSnap.exists()) {
    throw new Error('Item not found');
  }

  const item = docSnap.data();

  // Verify ownership
  if (item.userId !== userId) {
    throw new Error('Unauthorized: You do not own this item');
  }

  const history = item.history || [];
  if (historyIndex < 0 || historyIndex >= history.length) {
    throw new Error('Invalid history index');
  }

  const selectedVersion = history[historyIndex];

  // Create new item with the selected version
  const newItem = {
    title: item.title ? `${item.title} (Split)` : 'Split Image',
    name: item.name || null,
    description: item.description || '',
    type: item.type || 'image',
    imageUrl: selectedVersion.imageUrl,
    tags: [...(item.tags || [])],
    isFavorite: false,
    usageCount: 0,
    lastUsedAt: null,
    parentItemId: itemId, // Link to parent item
    history: [{
      imageUrl: selectedVersion.imageUrl,
      prompt: selectedVersion.prompt || null,
      refinedAt: new Date(),
      isOriginal: true // This becomes the original for the new item
    }],
    userId,
    brandId,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp()
  };

  const newDocRef = await addDoc(collection(db, LIBRARY_COLLECTION), newItem);
  return newDocRef.id;
}

/**
 * Update a specific carousel card's image with a refined version
 * @param {string} itemId - The carousel item document ID
 * @param {number} cardIndex - The index of the card to update
 * @param {string} newImageUrl - The URL of the refined image
 * @param {string} prompt - The refinement prompt used
 * @param {string} userId - The user ID (for verification)
 * @returns {Promise<Object>} - The updated item
 */
export async function updateCarouselCardImage(itemId, cardIndex, newImageUrl, prompt, userId) {
  const docRef = doc(db, LIBRARY_COLLECTION, itemId);
  const docSnap = await getDoc(docRef);

  if (!docSnap.exists()) {
    throw new Error('Item not found');
  }

  const item = docSnap.data();

  // Verify ownership
  if (item.userId !== userId) {
    throw new Error('Unauthorized: You do not own this item');
  }

  // Verify this is a carousel item
  if (item.type !== 'carousel' || !item.carouselImages || !Array.isArray(item.carouselImages)) {
    throw new Error('Item is not a carousel or has no carousel images');
  }

  // Verify card index is valid
  if (cardIndex < 0 || cardIndex >= item.carouselImages.length) {
    throw new Error('Invalid card index');
  }

  // Initialize card history if needed
  const card = item.carouselImages[cardIndex];
  let cardHistory = card.history || [];
  if (cardHistory.length === 0 && card.imageUrl) {
    cardHistory = [{
      imageUrl: card.imageUrl,
      prompt: null,
      refinedAt: new Date(),
      isOriginal: true
    }];
  }

  // Add new version to card history
  cardHistory.push({
    imageUrl: newImageUrl,
    prompt: prompt,
    refinedAt: new Date(),
    isOriginal: false
  });

  // Update the carousel card with new image and history
  const updatedCarouselImages = [...item.carouselImages];
  updatedCarouselImages[cardIndex] = {
    ...card,
    imageUrl: newImageUrl,
    history: cardHistory
  };

  // Also update the main imageUrl if this is the first card (preview image)
  const updates = {
    carouselImages: updatedCarouselImages,
    updatedAt: serverTimestamp()
  };
  if (cardIndex === 0) {
    updates.imageUrl = newImageUrl;
  }

  await updateDoc(docRef, updates);

  // Build return object without serverTimestamp (which is a sentinel)
  const returnItem = {
    id: docSnap.id,
    ...item,
    carouselImages: updatedCarouselImages,
    updatedAt: new Date()
  };
  if (cardIndex === 0) {
    returnItem.imageUrl = newImageUrl;
  }

  return returnItem;
}

/**
 * Add an upscaled version to a carousel card's history
 * @param {string} itemId - The carousel item document ID
 * @param {number} cardIndex - The index of the card to update
 * @param {string} upscaledImageUrl - The new upscaled image URL
 * @param {Object} upscaleMetadata - Metadata about the upscale operation
 * @param {string} upscaleMetadata.factor - The upscale factor (x2, x3, x4)
 * @param {Object} upscaleMetadata.originalDimensions - { width, height }
 * @param {Object} upscaleMetadata.upscaledDimensions - { width, height }
 * @param {string} userId - The user ID (for verification)
 * @returns {Promise<Object>} - The updated carousel item
 */
export async function addUpscaledCarouselCard(itemId, cardIndex, upscaledImageUrl, upscaleMetadata, userId) {
  const docRef = doc(db, LIBRARY_COLLECTION, itemId);
  const docSnap = await getDoc(docRef);

  if (!docSnap.exists()) {
    throw new Error('Item not found');
  }

  const item = docSnap.data();

  // Verify ownership
  if (item.userId !== userId) {
    throw new Error('Unauthorized: You do not own this item');
  }

  // Verify this is a carousel item
  if (item.type !== 'carousel' || !item.carouselImages || !Array.isArray(item.carouselImages)) {
    throw new Error('Item is not a carousel or has no carousel images');
  }

  // Verify card index is valid
  if (cardIndex < 0 || cardIndex >= item.carouselImages.length) {
    throw new Error('Invalid card index');
  }

  // Initialize card history if needed
  const card = item.carouselImages[cardIndex];
  let cardHistory = card.history || [];
  if (cardHistory.length === 0 && card.imageUrl) {
    cardHistory = [{
      imageUrl: card.imageUrl,
      prompt: null,
      refinedAt: new Date(),
      isOriginal: true
    }];
  }

  // Add upscaled version to card history
  cardHistory.push({
    imageUrl: upscaledImageUrl,
    prompt: `Upscaled ${upscaleMetadata.factor}`,
    refinedAt: new Date(),
    isOriginal: false,
    isUpscaled: true,
    upscaleMetadata: {
      factor: upscaleMetadata.factor,
      originalDimensions: upscaleMetadata.originalDimensions,
      upscaledDimensions: upscaleMetadata.upscaledDimensions
    }
  });

  // Update the carousel card with new image and history
  const updatedCarouselImages = [...item.carouselImages];
  updatedCarouselImages[cardIndex] = {
    ...card,
    imageUrl: upscaledImageUrl,
    history: cardHistory
  };

  // Also update the main imageUrl if this is the first card (preview image)
  const updates = {
    carouselImages: updatedCarouselImages,
    updatedAt: serverTimestamp()
  };
  if (cardIndex === 0) {
    updates.imageUrl = upscaledImageUrl;
  }

  await updateDoc(docRef, updates);

  // Build return object without serverTimestamp (which is a sentinel)
  const returnItem = {
    id: docSnap.id,
    ...item,
    carouselImages: updatedCarouselImages,
    updatedAt: new Date()
  };
  if (cardIndex === 0) {
    returnItem.imageUrl = upscaledImageUrl;
  }

  return returnItem;
}

/**
 * Delete a specific version from an item's history
 * Cannot delete the original version or the currently active version
 * @param {string} itemId - The item document ID
 * @param {number} historyIndex - The index in the history array to delete
 * @param {string} userId - The user ID (for verification)
 * @returns {Promise<Object>} - The updated item
 */
export async function deleteHistoryVersion(itemId, historyIndex, userId) {
  const docRef = doc(db, LIBRARY_COLLECTION, itemId);
  const docSnap = await getDoc(docRef);

  if (!docSnap.exists()) {
    throw new Error('Item not found');
  }

  const item = docSnap.data();

  // Verify ownership
  if (item.userId !== userId) {
    throw new Error('Unauthorized: You do not own this item');
  }

  const history = item.history || [];
  if (historyIndex < 0 || historyIndex >= history.length) {
    throw new Error('Invalid history index');
  }

  const versionToDelete = history[historyIndex];

  // Prevent deleting the original
  if (versionToDelete.isOriginal) {
    throw new Error('Cannot delete the original version');
  }

  // Prevent deleting the currently active version
  if (versionToDelete.imageUrl === item.imageUrl) {
    throw new Error('Cannot delete the currently active version. Switch to a different version first.');
  }

  // Remove the version from history
  const newHistory = history.filter((_, idx) => idx !== historyIndex);

  await updateDoc(docRef, {
    history: newHistory,
    updatedAt: serverTimestamp()
  });

  return {
    id: docSnap.id,
    ...item,
    history: newHistory
  };
}

// ============================================
// FOLDER MANAGEMENT FUNCTIONS
// ============================================

const FOLDERS_COLLECTION = 'libraryFolders';

/**
 * Create a new folder
 * @param {Object} folderData - The folder data { name, color?, parentFolderId? }
 * @param {string} userId - The user ID
 * @param {string} brandId - The brand ID
 * @param {string} itemType - The type of items this folder holds ('image', 'ad_creative', 'casting', 'location')
 * @returns {Promise<string>} - The folder document ID
 */
export async function createFolder(folderData, userId, brandId, itemType = 'image') {
  if (!userId || !brandId || !folderData.name) {
    throw new Error('userId, brandId, and folder name are required');
  }

  const folderDoc = {
    name: folderData.name,
    color: folderData.color || '#6366f1', // Default indigo color
    itemType,
    userId,
    brandId,
    itemCount: 0,
    parentFolderId: folderData.parentFolderId || null, // Support nested folders
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp()
  };

  const docRef = await addDoc(collection(db, FOLDERS_COLLECTION), folderDoc);
  return docRef.id;
}

/**
 * Get all folders for a user/brand
 * @param {string} userId - The user ID
 * @param {string} brandId - The brand ID
 * @param {string} itemType - Filter by item type (optional)
 * @param {string|null} parentFolderId - Filter by parent folder ID (null for root folders, undefined for all)
 * @returns {Promise<Array>} - Array of folder objects
 */
export async function getFolders(userId, brandId, itemType = null, parentFolderId = undefined) {
  if (!userId || !brandId) {
    return [];
  }

  // Build base constraints
  const constraints = [
    where('userId', '==', userId),
    where('brandId', '==', brandId)
  ];

  if (itemType) {
    constraints.push(where('itemType', '==', itemType));
  }

  constraints.push(orderBy('name', 'asc'));

  const q = query(collection(db, FOLDERS_COLLECTION), ...constraints);
  const snapshot = await getDocs(q);

  let folders = snapshot.docs.map(doc => ({
    id: doc.id,
    ...doc.data()
  }));

  // Filter by parentFolderId in memory (null = root folders, undefined = all folders)
  if (parentFolderId !== undefined) {
    folders = folders.filter(folder =>
      parentFolderId === null
        ? !folder.parentFolderId
        : folder.parentFolderId === parentFolderId
    );
  }

  return folders;
}

/**
 * Update a folder
 * @param {string} folderId - The folder ID
 * @param {Object} updates - The updates { name?, color? }
 * @param {string} userId - The user ID
 * @returns {Promise<void>}
 */
export async function updateFolder(folderId, updates, userId) {
  if (!folderId || !userId) {
    throw new Error('folderId and userId are required');
  }

  const docRef = doc(db, FOLDERS_COLLECTION, folderId);
  const docSnap = await getDoc(docRef);

  if (!docSnap.exists()) {
    throw new Error('Folder not found');
  }

  if (docSnap.data().userId !== userId) {
    throw new Error('Unauthorized to update this folder');
  }

  const allowedUpdates = {};
  if (updates.name) allowedUpdates.name = updates.name;
  if (updates.color) allowedUpdates.color = updates.color;
  if (updates.parentFolderId !== undefined) allowedUpdates.parentFolderId = updates.parentFolderId;
  allowedUpdates.updatedAt = serverTimestamp();

  await updateDoc(docRef, allowedUpdates);
}

/**
 * Move a folder to a different parent folder
 * @param {string} folderId - The folder ID to move
 * @param {string|null} targetParentFolderId - The new parent folder ID (null for root)
 * @param {string} userId - The user ID
 * @returns {Promise<void>}
 */
export async function moveFolderToFolder(folderId, targetParentFolderId, userId) {
  if (!folderId || !userId) {
    throw new Error('folderId and userId are required');
  }

  // Prevent moving folder into itself
  if (folderId === targetParentFolderId) {
    throw new Error('Cannot move a folder into itself');
  }

  const docRef = doc(db, FOLDERS_COLLECTION, folderId);
  const docSnap = await getDoc(docRef);

  if (!docSnap.exists()) {
    throw new Error('Folder not found');
  }

  if (docSnap.data().userId !== userId) {
    throw new Error('Unauthorized to move this folder');
  }

  // If moving to a subfolder, verify it's not a descendant of the folder being moved
  if (targetParentFolderId) {
    const targetRef = doc(db, FOLDERS_COLLECTION, targetParentFolderId);
    const targetSnap = await getDoc(targetRef);

    if (!targetSnap.exists()) {
      throw new Error('Target folder not found');
    }

    // Check for circular reference by traversing up the parent chain
    let currentParentId = targetSnap.data().parentFolderId;
    while (currentParentId) {
      if (currentParentId === folderId) {
        throw new Error('Cannot move a folder into its own subfolder');
      }
      const parentRef = doc(db, FOLDERS_COLLECTION, currentParentId);
      const parentSnap = await getDoc(parentRef);
      if (!parentSnap.exists()) break;
      currentParentId = parentSnap.data().parentFolderId;
    }
  }

  await updateDoc(docRef, {
    parentFolderId: targetParentFolderId || null,
    updatedAt: serverTimestamp()
  });
}

/**
 * Get the folder path (breadcrumb trail) from root to the given folder
 * @param {string} folderId - The folder ID
 * @param {string} userId - The user ID
 * @returns {Promise<Array>} - Array of folder objects from root to current folder
 */
export async function getFolderPath(folderId, userId) {
  if (!folderId || !userId) {
    return [];
  }

  const path = [];
  let currentFolderId = folderId;

  while (currentFolderId) {
    const folderRef = doc(db, FOLDERS_COLLECTION, currentFolderId);
    const folderSnap = await getDoc(folderRef);

    if (!folderSnap.exists() || folderSnap.data().userId !== userId) {
      break;
    }

    const folderData = { id: folderSnap.id, ...folderSnap.data() };
    path.unshift(folderData); // Add to beginning to build path from root
    currentFolderId = folderData.parentFolderId;
  }

  return path;
}

/**
 * Delete a folder (items and subfolders are moved to the parent folder or root)
 * @param {string} folderId - The folder ID
 * @param {string} userId - The user ID
 * @returns {Promise<void>}
 */
export async function deleteFolder(folderId, userId) {
  if (!folderId || !userId) {
    throw new Error('folderId and userId are required');
  }

  const docRef = doc(db, FOLDERS_COLLECTION, folderId);
  const docSnap = await getDoc(docRef);

  if (!docSnap.exists()) {
    throw new Error('Folder not found');
  }

  const folderData = docSnap.data();
  if (folderData.userId !== userId) {
    throw new Error('Unauthorized to delete this folder');
  }

  const parentFolderId = folderData.parentFolderId || null;
  const batch = writeBatch(db);

  // Move items in this folder to the parent folder (or root)
  const itemsQuery = query(
    collection(db, LIBRARY_COLLECTION),
    where('folderId', '==', folderId),
    where('userId', '==', userId)
  );

  const itemsSnapshot = await getDocs(itemsQuery);
  itemsSnapshot.docs.forEach(itemDoc => {
    batch.update(itemDoc.ref, { folderId: parentFolderId, updatedAt: serverTimestamp() });
  });

  // Move subfolders to the parent folder (or root)
  const subfoldersQuery = query(
    collection(db, FOLDERS_COLLECTION),
    where('parentFolderId', '==', folderId),
    where('userId', '==', userId)
  );

  const subfoldersSnapshot = await getDocs(subfoldersQuery);
  subfoldersSnapshot.docs.forEach(subfolderDoc => {
    batch.update(subfolderDoc.ref, { parentFolderId: parentFolderId, updatedAt: serverTimestamp() });
  });

  // Delete the folder
  batch.delete(docRef);
  await batch.commit();
}

/**
 * Move items to a folder
 * @param {Array<string>} itemIds - Array of item IDs to move
 * @param {string|null} folderId - The target folder ID (null to move to root)
 * @param {string} userId - The user ID
 * @returns {Promise<void>}
 */
export async function moveItemsToFolder(itemIds, folderId, userId) {
  if (!itemIds || itemIds.length === 0 || !userId) {
    throw new Error('itemIds and userId are required');
  }

  const batch = writeBatch(db);
  const folderUpdates = {}; // Track folder item count changes

  for (const itemId of itemIds) {
    const itemRef = doc(db, LIBRARY_COLLECTION, itemId);
    const itemSnap = await getDoc(itemRef);

    if (!itemSnap.exists()) {
      continue;
    }

    if (itemSnap.data().userId !== userId) {
      continue;
    }

    const oldFolderId = itemSnap.data().folderId;

    // Track count changes for old folder
    if (oldFolderId) {
      folderUpdates[oldFolderId] = (folderUpdates[oldFolderId] || 0) - 1;
    }

    // Track count changes for new folder
    if (folderId) {
      folderUpdates[folderId] = (folderUpdates[folderId] || 0) + 1;
    }

    batch.update(itemRef, {
      folderId: folderId || null,
      updatedAt: serverTimestamp()
    });
  }

  // Update folder item counts
  for (const [folderIdKey, countChange] of Object.entries(folderUpdates)) {
    const folderRef = doc(db, FOLDERS_COLLECTION, folderIdKey);
    batch.update(folderRef, {
      itemCount: increment(countChange),
      updatedAt: serverTimestamp()
    });
  }

  await batch.commit();
}

/**
 * Get items in a folder (or root items if folderId is null)
 * @param {string} userId - The user ID
 * @param {string} brandId - The brand ID
 * @param {string|null} folderId - The folder ID (null for root items)
 * @param {Object} options - Query options (type, isFavorite, tags, orderByField)
 * @returns {Promise<Array>} - Array of items
 */
export async function getItemsInFolder(userId, brandId, folderId, options = {}) {
  if (!userId || !brandId) {
    return [];
  }

  // Build query constraints - keep simple to avoid composite index issues
  // We'll filter by folderId in memory to avoid needing folderId + orderBy index
  const constraints = [
    where('userId', '==', userId),
    where('brandId', '==', brandId)
  ];

  // Only use single type filter in query (array filtering done in memory to avoid composite index)
  if (options.type && !Array.isArray(options.type)) {
    constraints.push(where('type', '==', options.type));
  }

  if (options.isFavorite) {
    constraints.push(where('isFavorite', '==', true));
  }

  if (options.tags && options.tags.length > 0) {
    constraints.push(where('tags', 'array-contains-any', options.tags));
  }

  const orderField = options.orderByField || 'createdAt';
  constraints.push(orderBy(orderField, 'desc'));

  const q = query(collection(db, LIBRARY_COLLECTION), ...constraints);
  const snapshot = await getDocs(q);

  let items = snapshot.docs.map(doc => ({
    id: doc.id,
    ...doc.data()
  }));

  // Filter by type array in memory (avoids composite index requirement)
  if (options.type && Array.isArray(options.type)) {
    items = items.filter(item => options.type.includes(item.type));
  }

  // Filter by folderId in memory (avoids composite index requirement for folderId + orderBy)
  if (folderId) {
    items = items.filter(item => item.folderId === folderId);
  } else {
    // Root items - items without a folderId or with null folderId
    items = items.filter(item => !item.folderId);
  }

  return items;
}

/**
 * Get folder with preview thumbnails (first 4 items)
 * @param {string} folderId - The folder ID
 * @param {string} userId - The user ID
 * @returns {Promise<Object>} - Folder object with thumbnails array
 */
export async function getFolderWithThumbnails(folderId, userId) {
  if (!folderId || !userId) {
    throw new Error('folderId and userId are required');
  }

  const folderRef = doc(db, FOLDERS_COLLECTION, folderId);
  const folderSnap = await getDoc(folderRef);

  if (!folderSnap.exists()) {
    throw new Error('Folder not found');
  }

  const folderData = {
    id: folderSnap.id,
    ...folderSnap.data()
  };

  // Get items for thumbnails - query by userId only, filter folderId in memory
  // to avoid composite index requirement for folderId + orderBy
  const itemsQuery = query(
    collection(db, LIBRARY_COLLECTION),
    where('userId', '==', userId),
    where('brandId', '==', folderData.brandId),
    orderBy('createdAt', 'desc')
  );

  const itemsSnapshot = await getDocs(itemsQuery);

  // Filter by folderId in memory - get direct items in this folder
  const directFolderItems = itemsSnapshot.docs
    .filter(doc => doc.data().folderId === folderId);

  let thumbnails = directFolderItems
    .slice(0, 4)
    .map(doc => doc.data().imageUrl)
    .filter(url => url);

  // Get subfolders of this folder
  const subfoldersQuery = query(
    collection(db, FOLDERS_COLLECTION),
    where('parentFolderId', '==', folderId),
    where('userId', '==', userId)
  );
  const subfoldersSnapshot = await getDocs(subfoldersQuery);
  const subfolderIds = subfoldersSnapshot.docs.map(doc => doc.id);
  const subfolderCount = subfolderIds.length;

  // If we have fewer than 4 thumbnails, try to get items from subfolders
  if (thumbnails.length < 4 && subfolderCount > 0) {
    // Get items from subfolders
    const subfolderItems = itemsSnapshot.docs
      .filter(doc => subfolderIds.includes(doc.data().folderId))
      .slice(0, 4 - thumbnails.length)
      .map(doc => doc.data().imageUrl)
      .filter(url => url);

    thumbnails = [...thumbnails, ...subfolderItems].slice(0, 4);
  }

  // Count includes direct items + subfolders (each subfolder counts as 1 item)
  return {
    ...folderData,
    thumbnails,
    itemCount: directFolderItems.length + subfolderCount
  };
}

/**
 * Get all folders with their thumbnails
 * @param {string} userId - The user ID
 * @param {string} brandId - The brand ID
 * @param {string} itemType - Filter by item type
 * @param {string|null} parentFolderId - Filter by parent folder ID (null for root, undefined for all)
 * @returns {Promise<Array>} - Array of folders with thumbnails
 */
export async function getFoldersWithThumbnails(userId, brandId, itemType, parentFolderId = undefined) {
  const folders = await getFolders(userId, brandId, itemType, parentFolderId);

  // Get thumbnails for each folder
  const foldersWithThumbnails = await Promise.all(
    folders.map(async (folder) => {
      try {
        const folderWithThumbs = await getFolderWithThumbnails(folder.id, userId);
        return folderWithThumbs;
      } catch (error) {
        console.error(`Error getting thumbnails for folder ${folder.id}:`, error);
        return { ...folder, thumbnails: [], itemCount: 0 };
      }
    })
  );

  return foldersWithThumbnails;
}

/**
 * Get subfolder count for a folder
 * @param {string} folderId - The folder ID
 * @param {string} userId - The user ID
 * @returns {Promise<number>} - Number of subfolders
 */
export async function getSubfolderCount(folderId, userId) {
  if (!folderId || !userId) return 0;

  const subfoldersQuery = query(
    collection(db, FOLDERS_COLLECTION),
    where('parentFolderId', '==', folderId),
    where('userId', '==', userId)
  );

  const snapshot = await getDocs(subfoldersQuery);
  return snapshot.size;
}

/**
 * Get all items in a folder recursively (including items in subfolders)
 * Returns only the latest version of each item (using imageUrl, not history)
 * @param {string} folderId - The folder ID
 * @param {string} userId - The user ID
 * @param {string} brandId - The brand ID
 * @returns {Promise<Array>} - Array of items with their folder path
 */
export async function getAllItemsInFolderRecursive(folderId, userId, brandId) {
  if (!folderId || !userId || !brandId) {
    return [];
  }

  // Get the folder to get its name
  const folderRef = doc(db, FOLDERS_COLLECTION, folderId);
  const folderSnap = await getDoc(folderRef);

  if (!folderSnap.exists()) {
    return [];
  }

  const folderData = folderSnap.data();
  const folderName = folderData.name || 'Folder';

  // Get all items for this user/brand
  const itemsQuery = query(
    collection(db, LIBRARY_COLLECTION),
    where('userId', '==', userId),
    where('brandId', '==', brandId)
  );
  const itemsSnapshot = await getDocs(itemsQuery);
  const allItems = itemsSnapshot.docs.map(doc => ({
    id: doc.id,
    ...doc.data()
  }));

  // Get all folders for this user/brand
  const foldersQuery = query(
    collection(db, FOLDERS_COLLECTION),
    where('userId', '==', userId),
    where('brandId', '==', brandId)
  );
  const foldersSnapshot = await getDocs(foldersQuery);
  const allFolders = foldersSnapshot.docs.map(doc => ({
    id: doc.id,
    ...doc.data()
  }));

  // Build folder tree to get all descendant folder IDs
  const getDescendantFolderIds = (parentId, folders) => {
    const descendants = [];
    const children = folders.filter(f => f.parentFolderId === parentId);
    for (const child of children) {
      descendants.push(child.id);
      descendants.push(...getDescendantFolderIds(child.id, folders));
    }
    return descendants;
  };

  // Get all folder IDs to include (the folder itself + all descendants)
  const folderIdsToInclude = [folderId, ...getDescendantFolderIds(folderId, allFolders)];

  // Build folder path map for naming files
  const getFolderPath = (itemFolderId) => {
    if (itemFolderId === folderId) return '';

    const pathParts = [];
    let currentId = itemFolderId;

    while (currentId && currentId !== folderId) {
      const folder = allFolders.find(f => f.id === currentId);
      if (folder) {
        pathParts.unshift(folder.name);
        currentId = folder.parentFolderId;
      } else {
        break;
      }
    }

    return pathParts.join('/');
  };

  // Filter items that are in the folder or any subfolder
  const items = allItems
    .filter(item => folderIdsToInclude.includes(item.folderId))
    .map(item => ({
      ...item,
      folderPath: getFolderPath(item.folderId),
      // Use imageUrl as the latest version
      downloadUrl: item.imageUrl
    }))
    .filter(item => item.downloadUrl); // Only include items with a valid URL

  return {
    folderName,
    items
  };
}
